import { ErrorsOr, mapErrorsOr, parseJson } from "@me8eon/errors";
import { CryptoMetadata, CryptoMetadataAndSecret } from "@enterprise_search/storage_crypto";
import { AxiosInstance, AxiosResponse } from "axios";
import { mustBeCryptoMetaDataAndSecret, mustBeHasSecret } from "./crypto.validate";


export type CryptoClientConfig = {
    base: string
    axios: AxiosInstance
}
export type SecretForFn = (jwtToken: string, meta: CryptoMetadata) => Promise<ErrorsOr<string>>
export type InitialMetaFn = (jwtToken: string) => Promise<ErrorsOr<CryptoMetadataAndSecret>>;
export type CryptoClient = {
    secretFor: SecretForFn
    initialMeta: InitialMetaFn
}

export type HasSecret = { secret: string };

export const parseSecret = (response: AxiosResponse<any>): ErrorsOr<string> =>
    mapErrorsOr(parseJson<HasSecret>("Response data", { validate: mustBeHasSecret })(response.data),
        json => json.secret);

export const secretFor = ({ axios, base }: CryptoClientConfig): SecretForFn => {
    const url = `${base}/crypto/ongoing`;
    return async (jwtToken: string, meta: CryptoMetadata) => {
        const headers: Record<string, string> = { "Authorization": `Bearer ${jwtToken}`, "Content-Type": "application/json" };
        const response = await axios.post(url, JSON.stringify(meta, null, 2), { responseType: "text", headers, validateStatus: () => true });
        if (response.status >= 400)
            return { errors: [`Error getting secret: ${response.status} ${response.statusText}, ${JSON.stringify(response)}`] };
        return parseSecret(response);
    };
};

export function initialMeta({ axios, base }: CryptoClientConfig): InitialMetaFn {
    const url = `${base}/crypto/initial`;
    return async (jwtToken: string) => {
        const headers: Record<string, string> = { "Authorization": `Bearer ${jwtToken}`, "Content-Type": "application/json" };
        const response = await axios.post(url, {}, { responseType: "text", headers, validateStatus: () => true });
        if (response.status >= 400)
            return { errors: [`Error getting initial metadata: ${response.status} ${response.statusText}, ${JSON.stringify(response.data)}`] };
        const result = parseJson<CryptoMetadataAndSecret>("initialMeta",
            { validate: mustBeCryptoMetaDataAndSecret })(response.data);
        return result;
    };
}
export function createCryptoClient(config: CryptoClientConfig): CryptoClient {
    return {
        secretFor: secretFor(config),
        initialMeta: initialMeta(config),
    }
}